import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  width?: number | string;
  footer?: React.ReactNode;
}

export function Drawer({ open, onClose, title, children, width = 440, footer }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const applyTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme');
      if (panelRef.current) {
        if (theme) panelRef.current.setAttribute('data-theme', theme);
        else panelRef.current.removeAttribute('data-theme');
      }
    };
    applyTheme();
    const observer = new MutationObserver(applyTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [open]);

  return createPortal(
    <>
      <style>{`
        @keyframes drawer-in {
          0%   { transform: translateX(100%); }
          60%  { transform: translateX(-8px); }
          100% { transform: translateX(0); }
        }
        @keyframes drawer-out {
          from { transform: translateX(0); opacity: 1; }
          to   { transform: translateX(100%); opacity: 0; }
        }
        .drawer-overlay { position: fixed; inset: 0; z-index: 900; background: rgba(0,0,0,0.5); backdrop-filter: blur(12px) saturate(140%); -webkit-backdrop-filter: blur(12px) saturate(140%); }
        .drawer-overlay-hidden { display: none; }
        .drawer-panel {
          position: fixed; right: 0; top: 0; bottom: 0; z-index: 901;
          background: var(--glass-bg-elevated);
          backdrop-filter: blur(var(--lg-blur)) saturate(var(--lg-saturation));
          -webkit-backdrop-filter: blur(var(--lg-blur)) saturate(var(--lg-saturation));
          border-left: 1px solid rgba(255,255,255,0.15);
          display: flex; flex-direction: column;
          box-shadow: -8px 0 40px rgba(0,0,0,0.5);
          border-radius: var(--radius-container) 0 0 var(--radius-container);
          overflow: hidden;
        }
        .drawer-panel-open { animation: drawer-in 220ms cubic-bezier(0.16,1,0.3,1) both; }
        .drawer-panel-closed { animation: drawer-out 180ms ease-in both; }
        @media (max-width: 767px) {
          .drawer-panel { width: 100vw !important; border-left: none; border-radius: var(--radius-container) var(--radius-container) 0 0; top: auto; bottom: 0; max-height: 90dvh; }
        }
      `}</style>

      <div
        className={open ? 'drawer-overlay' : 'drawer-overlay-hidden'}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`drawer-panel ${open ? 'drawer-panel-open' : 'drawer-panel-closed'}`}
        style={{ width, display: open ? 'flex' : 'none' }}
      >
        {title && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '1rem 1.25rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            flexShrink: 0,
          }}>
            <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--color-text)' }}>{title}</h3>
            <button
              onClick={onClose}
              aria-label="Chiudi"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-text-muted)', padding: '0.25rem',
                borderRadius: 'var(--radius-interactive)', display: 'flex',
              }}
            >
              <X size={18} />
            </button>
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', padding: '1.25rem' }}>
          {children}
        </div>

        {footer && (
          <div style={{
            padding: '1rem 1.25rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex', gap: '0.5rem', justifyContent: 'flex-end',
            flexShrink: 0,
          }}>
            {footer}
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
